import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { clearServiceSession, getServiceCommandStatus, hasServiceCapability, sendServiceCommand } from "../../services/kioskServiceSession";
import ServiceDialog, { serviceError, serviceSessionExpired } from "./ServiceDialog";

const COMMANDS = [
    { id: "net_diag", group: "diagnostics" },
    { id: "collect_diagnostics", group: "diagnostics", params: { lines: 50 } },
    { id: "refresh_page", group: "recovery", confirm: true },
    { id: "clear_cache", group: "recovery", confirm: true },
    { id: "app_restart", group: "recovery", confirm: true },
    { id: "spooler_restart", group: "recovery", confirm: true },
    { id: "print_queue_clear", group: "recovery", confirm: true, danger: true },
];
const TERMINAL = new Set(["succeeded", "failed", "expired", "canceled", "sent_legacy"]);
const STATUSES = new Set(["sending", "queued", "sent", "delivered", "acknowledged", "acked", "running", "succeeded", "failed", "expired", "canceled", "sent_legacy", "unknown"]);

export default function ServiceCommandsPanel({ category, busy, setBusy }) {
    const { t } = useTranslation();
    const titleOf = (id) => t(`service.commands.items.${id}.title`);
    const noteOf = (id) => t(`service.commands.items.${id}.note`);
    // Server errors arrive as text; our own fallbacks are translation keys.
    const text = (message) => message.startsWith("service.") ? t(message) : message;
    const [confirm, setConfirm] = useState(null);
    const [result, setResult] = useState(null);
    const [warning, setWarning] = useState("");
    const [watch, setWatch] = useState(null);
    const sending = useRef(false);
    const mounted = useRef(false);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

    useEffect(() => {
        if (!watch) return;
        let canceled = false;
        let timer;
        let attempts = 0;
        const poll = async () => {
            try {
                const status = await getServiceCommandStatus(watch.id);
                if (canceled) return;
                setResult((previous) => ({ ...previous, ...status }));
                if (TERMINAL.has(status.status)) { setBusy(""); return; }
                attempts += 1;
                if (attempts >= 20) {
                    setWarning("service.commands.no_reply");
                    setBusy(""); return;
                }
                timer = setTimeout(poll, 1500);
            } catch (err) {
                if (canceled) return;
                if (serviceSessionExpired(err)) clearServiceSession();
                setWarning(serviceError(err, "service.commands.connection_lost"));
                setBusy("");
            }
        };
        timer = setTimeout(poll, 1500);
        return () => { canceled = true; clearTimeout(timer); };
    }, [watch, setBusy]);

    const run = async (item) => {
        if (busy || sending.current || !hasServiceCapability("service_commands")) return;
        sending.current = true;
        setConfirm(null); setWatch(null); setWarning("");
        setBusy(titleOf(item.id)); setResult({ command: item.id, status: "sending" });
        try {
            const accepted = await sendServiceCommand(item.id, item.params || {});
            if (!mounted.current) return;
            const status = accepted.status || (accepted.queued ? "queued" : "sent");
            setResult({ command: item.id, command_id: accepted.command_id, status });
            if (accepted.command_id && !TERMINAL.has(status)) setWatch({ id: accepted.command_id });
            else {
                setBusy("");
                if (!TERMINAL.has(status)) setWarning("service.commands.no_id");
            }
        } catch (err) {
            if (!mounted.current) return;
            if (serviceSessionExpired(err)) clearServiceSession();
            setResult({ command: item.id, status: err.response && err.response.status < 500 ? "failed" : "unknown" });
            setWarning(serviceError(err, "service.commands.send_error"));
            setBusy("");
        } finally { sending.current = false; }
    };
    const title = result?.command ? titleOf(result.command) : null;
    const statusText = result ? (STATUSES.has(result.status) ? t(`service.commands.status.${result.status}`) : t("service.commands.result_waiting")) : t("service.commands.result_empty");
    return <>
        <div className="service-command-layout">
            <div className="service-scroll">
                <div className="service-card mb-[1.5vh]">
                    <h2>{t(`service.commands.${category}_title`)}</h2>
                    <p className="service-subtitle">{t(`service.commands.${category}_text`)}</p>
                    {!hasServiceCapability("service_commands") && <p className="service-message mt-[1vh]">{t("service.commands.unavailable")}</p>}
                </div>
                <div className="service-grid">
                    {COMMANDS.filter((item) => item.group === category).map((item) => <article key={item.id} className="service-card service-command-card">
                        <h3>{titleOf(item.id)}</h3><p className="service-subtitle">{noteOf(item.id)}</p>
                        <button data-analytics="servicecommandspanel.service.commands.running.1" className={`service-button ${item.danger ? "danger" : "primary"}`} aria-label={titleOf(item.id)} disabled={Boolean(busy) || !hasServiceCapability("service_commands")} onClick={() => item.confirm ? setConfirm(item) : run(item)}>
                            {busy === titleOf(item.id) ? t("service.commands.running") : t(item.group === "diagnostics" ? "service.commands.run_check" : "service.commands.choose_action")}
                        </button>
                    </article>)}
                </div>
            </div>
            <section className="service-card service-result" aria-label={t("service.commands.result_label")}>
                <div className="service-toolbar">
                    <div><h2>{title || t("service.commands.result_title")}</h2><p className="service-subtitle" role="status">{statusText}</p></div>
                    {result?.command_id && !TERMINAL.has(result.status) && <button data-analytics="servicecommandspanel.service.commands.check_status.2" className="service-button" disabled={Boolean(busy)} onClick={() => { setWarning(""); setBusy(title); setWatch({ id: result.command_id }); }}>{t("service.commands.check_status")}</button>}
                </div>
                {warning && <p className="service-message" role="status">{text(warning)}</p>}
                {result?.error_message && <p className="service-message error" role="alert">{result.error_message}</p>}
                {result?.status === "sent_legacy" && <p className="service-subtitle">{t("service.commands.legacy")}</p>}
                {result?.result != null && <details><summary className="font-bold cursor-pointer">{t("service.commands.details")}</summary><pre>{typeof result.result === "string" ? result.result : JSON.stringify(result.result, null, 2)}</pre></details>}
            </section>
        </div>
        {confirm && <ServiceDialog title={`${titleOf(confirm.id)}?`} onClose={() => setConfirm(null)}>
            <p className="service-subtitle">{noteOf(confirm.id)}</p>
            <p className="service-message mt-[2vh]">{t("service.commands.confirm_warning")}</p>
            <div className="service-actions"><button data-analytics="servicecommandspanel.service.commands.cancel.3" className="service-button" onClick={() => setConfirm(null)}>{t("service.commands.cancel")}</button><button data-analytics="servicecommandspanel.service.commands.confirm.4" className={`service-button ${confirm.danger ? "danger" : "primary"}`} disabled={Boolean(busy)} onClick={() => run(confirm)}>{t("service.commands.confirm")}</button></div>
        </ServiceDialog>}
    </>;
}
